import { z } from "zod";
import { zInstanteUtc, zUsuarioPublico } from "./dominio.js";
import { zNota } from "./caderno.js";
import { markdownDeHtml } from "./conteudo.js";
import { dataCurta, instantePorExtenso } from "./formato.js";
/**
 * Exportação (SPEC §7, F3 — RN-35).
 *
 * Com a cópia dos backups para fora da VM adiada por decisão, este arquivo é a
 * **única saída de dados que existe**. Duas exigências decorrem disso, e elas
 * ordenam tudo aqui: ser completo e ser legível fora do app.
 *
 * ## Por que o corpo é um mapa de tabela → linhas
 *
 * A tentação seria escrever um schema Zod coluna a coluna para cada uma das
 * tabelas. Seria reescrever à mão, num segundo lugar, o schema que o Drizzle já
 * declara — exatamente o que o invariante 1 proíbe, e com a agravante de que a
 * cópia envelheceria em silêncio: coluna nova no banco, ausente no export, e
 * ninguém sabe até o dia em que o arquivo precisa servir para alguma coisa.
 *
 * Então o payload é validado na **forma** (mapa de tabela para lista de linhas)
 * e a garantia de completude vem de onde ela pode ser automática: de um teste
 * que compara as chaves exportadas com as tabelas que existem no `esquema` do
 * Drizzle. Tabela nova quebra o teste no dia em que nasce.
 *
 * ## Por que as notas saem à parte
 *
 * `nota` não viaja em `tabelas`: vem em `notas`, com o nome da trilha e o
 * caminho da categoria já resolvidos. A linha crua do banco tem só ids, e um
 * caderno em que toda nota diz `categoriaId: "a3f…"` não é legível fora do app
 * — que é metade do requisito. Sai uma vez, no formato que serve.
 */
/** Uma linha de tabela, como veio do banco. */
export const zLinhaExportada = z.record(z.string(), z.unknown());
export const zNotaExportada = zNota.extend({
    /** Nome da trilha, não o id: o arquivo é lido por gente. */
    trilha: z.string(),
});
export const zExportacao = z.object({
    gerado: z.object({
        em: zInstanteUtc,
        versaoApp: z.string(),
        /**
         * Quem exportou. O `fusoHorario` daqui não é enfeite: é o fuso em que os
         * dias de `diario` foram decididos (invariante 16), e sem ele as datas das
         * tabelas derivadas ficam sem referência para quem lê o arquivo.
         */
        usuario: zUsuarioPublico,
    }),
    /** As notas do caderno, com trilha e categoria resolvidas. */
    notas: z.array(zNotaExportada),
    /** Todo o resto, uma chave por tabela do banco. */
    tabelas: z.record(z.string(), z.array(zLinhaExportada)),
});
/** Quantas linhas o arquivo carrega, por tabela — o resumo que a tela mostra. */
export function contarLinhas(e) {
    const contagens = Object.entries(e.tabelas).map(([tabela, linhas]) => ({ tabela, linhas: linhas.length }));
    contagens.push({ tabela: "nota", linhas: e.notas.length });
    return contagens.sort((a, b) => a.tabela.localeCompare(b.tabela, "pt-BR"));
}
/**
 * O nome do arquivo carrega o dia **do usuário**, não o dia UTC (invariante 16):
 * exportar às 22h de São Paulo não pode gravar o arquivo de amanhã.
 *
 * `conta` entra no nome quando o destino é uma pasta compartilhada — que é o
 * caso do `exportar` por terminal, gravando em `/opt/noesis/backup`. Sem ela,
 * duas contas exportando no mesmo dia gravam o mesmo caminho, e a segunda
 * apaga o caderno da primeira sem dizer nada. O download pela tela não passa
 * `conta`: ele nasce da sessão de quem clicou e vai para a máquina dele.
 */
export function nomeDoArquivo(extensao, dia, conta) {
    const quem = conta ? `${apelidoDeConta(conta)}-` : "";
    return extensao === "md" ? `noesis-caderno-${quem}${dia}.md` : `noesis-${quem}${dia}.json`;
}
/** E-mail vira pedaço de nome de arquivo: minúsculas, só `a-z0-9-`, sem repetir `-`. */
export function apelidoDeConta(conta) {
    return conta
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}
/**
 * O caderno inteiro num Markdown corrido, legível em qualquer editor — é o item
 * do checklist do SPEC §8.
 *
 * O corpo de cada nota é **derivado do `html` guardado** (RN-35, RN-39). O
 * Markdown deixou de ser a fonte da verdade quando o editor ganhou cor, realce,
 * tamanho e alinhamento — formatação que Markdown não sabe escrever.
 *
 * **A perda é real e precisa ser avisada antes**, não descoberta no arquivo:
 * essas quatro coisas, mais sublinhado e tachado, somem aqui. A tela de
 * exportação pergunta `temFormatacaoDecorativa` nota a nota e diz quantas
 * perdem formatação antes de gerar. O que sobrevive é a estrutura — título,
 * lista, citação, código, ênfase, força e link.
 *
 * O cabeçalho de cada nota continua sendo `##` e nada dentro do corpo é
 * reindentado ou promovido de nível.
 */
export function montarCadernoMarkdown(e) {
    const partes = [];
    partes.push("# Caderno do Grifo");
    partes.push("");
    partes.push(`Exportado em ${instantePorExtenso(e.gerado.em, e.gerado.usuario.fusoHorario)} · ${e.gerado.usuario.nome}`);
    partes.push("");
    if (e.notas.length === 0) {
        // Estado vazio também no arquivo: um `.md` com só um título faz quem abre
        // desconfiar da exportação, em vez de do caderno.
        partes.push("_Nenhuma nota escrita até aqui._");
        partes.push("");
        return partes.join("\n");
    }
    // Agrupado por trilha, na ordem em que as notas chegaram já ordenadas pelo
    // servidor. Sem `sort` aqui: ordenar em dois lugares é ordenar em nenhum.
    let trilhaAtual = null;
    for (const nota of e.notas) {
        if (nota.trilha !== trilhaAtual) {
            trilhaAtual = nota.trilha;
            partes.push(`# ${trilhaAtual}`);
            partes.push("");
        }
        partes.push(`## ${nota.titulo}`);
        partes.push("");
        partes.push(`_${linhaDeMetadados(nota, e.gerado.usuario.fusoHorario)}_`);
        partes.push("");
        partes.push(markdownDeHtml(nota.html).trimEnd());
        partes.push("");
        // Separador entre notas. `---` logo depois de uma linha de texto viraria
        // sublinhado de título em Markdown; a linha em branco acima impede isso.
        partes.push("---");
        partes.push("");
    }
    return partes.join("\n");
}
function linhaDeMetadados(nota, fuso) {
    const campos = [];
    campos.push(nota.categoria.length > 0 ? nota.categoria.join(" › ") : "Sem categoria");
    campos.push(`atualizada em ${dataCurta(nota.atualizadoEm, fuso)}`);
    if (nota.itensAnexados.length > 0) {
        const n = nota.itensAnexados.length;
        campos.push(`${n} ${n === 1 ? "item anexado" : "itens anexados"}`);
    }
    return campos.join(" · ");
}
//# sourceMappingURL=exportacao.js.map